import { Box, Button, Icon, Typography } from "@outschool/backpack";
import { fasFlag } from "@outschool/icons";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import { WebsiteRoutes } from "@outschool/routes";
import { gql, useApolloClient } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { TranslationWarning } from "@outschool/ui-components-website";
import React from "react";

// @ts-ignore - JSX import
import MessageModal from "../../MessageModal";
import ActivityAttributes from "../subcomponents/ActivityAttributes";
import HeadingMarker from "../subcomponents/HeadingMarker";
import ShowMoreModal from "../subcomponents/ShowMoreModal";
import { gradientFadeStyle } from "./consts";

export default function ActivityDetailDescription({
  activity,
  section,
  currentUser,
  hideReportListing = false,
  headerRef,
  isApprovalsView,
}: {
  activity: any;
  section?: any;
  currentUser?: any;
  defaultOpen?: boolean;
  hideReportListing?: boolean;
  isApprovalsView?: boolean;
  headerRef?: React.RefObject<HTMLElement>;
}) {
  const { isLoggedIn } = useSession();

  const client = useApolloClient();
  const websiteRoutes = new WebsiteRoutes({});

  const { translate } = useMachineTranslation();
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\ActivityDetailDescription",
  );
  const reportListingPrompt = isLoggedIn
    ? t`Please tell Outschool about inaccuracies, offensive content, or other issues with this class listing. For questions about the class, please message the teacher directly.`
    : t`Please tell Outschool about inaccuracies, offensive content, or other issues with this class listing. Please include your email address, since you are logged out. For questions about the class, please message the teacher directly.`;

  // experiments
  const translatedClassExperience = translate(
    activity.class_experience?.trim() ?? "",
  );

  return (
    <Box>
      <Box
        flex
        sx={{
          flexFlow: "row wrap",
          justifyContent: "space-between",
        }}
      >
        <TranslationWarning />
      </Box>
      <Box ref={headerRef}>
        <HeadingMarker />
        <Typography
          emphasized
          variant="h4"
          sx={{
            paddingBottom: 8,
          }}
          ref={headerRef}
        >
          {activity.isClub ? t`Group experience` : t`Class Experience`}
        </Typography>

        <ActivityAttributes activity={activity} />
        <Box>
          <Box
            component="pre"
            sx={{
              marginTop: 0,
              ...(!isApprovalsView && gradientFadeStyle),
            }}
          >
            {translatedClassExperience}
          </Box>

          <Box
            sx={{
              display: isApprovalsView ? "none" : "block",
            }}
          >
            <ShowMoreModal
              buttonLabel={t("Show More")}
              trackingName={"class_experience"}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1em",
                }}
              >
                <Typography emphasized variant="h4">
                  {activity.isClub ? t`Group experience` : t`Class Experience`}
                </Typography>

                <ActivityAttributes
                  sx={{ paddingY: 0, paddingTop: 0, paddingBottom: 0 }}
                  activity={activity}
                />

                <Box component="pre" sx={{ margin: 0 }}>
                  {translatedClassExperience}
                </Box>
                {!hideReportListing && (
                  <Box>
                    <MessageModal
                      client={client}
                      renderButton={(open: () => void) => (
                        <Button
                          variant="link"
                          onClick={open}
                          startIcon={<Icon icon={fasFlag} />}
                        >
                          {t("Report this listing")}
                        </Button>
                      )}
                      heading={t("Report this listing")}
                      prompt={reportListingPrompt}
                      toOutschool
                      user={currentUser}
                      activityUid={activity.uid}
                      sectionUid={section && section.uid}
                      context={{
                        // eslint-disable-next-line i18next/no-literal-string
                        action: "Report class listing",
                        activityTitle: activity.title,
                        activityUrl: websiteRoutes.sectionOrActivityUrl(
                          activity,
                          section,
                        ),
                      }}
                      hideEmail
                    />
                  </Box>
                )}
              </Box>
            </ShowMoreModal>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

ActivityDetailDescription.fragments = {
  activity: gql`
    fragment ActivityDetailDescription_activity on Activity {
      uid
      assessment
      class_experience
      externalResources {
        uid
        name
      }
      details {
        asyncTime
      }
      has_external_resources
      homework
      learning_goals
      materials
      parental_guidance
      sources
      title
      titleTranslated
      weekly_meetings
      welcomeAttachmentCount
      why_me
      duration_minutes
      clubGuidelines {
        text
      }
      suggestedWeeklyMeetings
      english_proficiency_level
    }
  `,
};
